// Pure text builders for the KPI compliance chart: the tooltip line and the
// chart's spoken label.
//
// They sit in their own leaf module because Recharts never calls a tooltip
// renderer under jsdom's zero-width ResponsiveContainer, so wording left
// inside the chart component would be untestable.
//
// A month with no reported result says so. It is the one distinction this
// chart exists to keep: a contract where nobody logged anything and a
// contract where every KPI failed are opposite facts, and rendering both as
// 0% would state the second when the first is true.

import type { KpiComplianceValue } from "../../../../types/report";
import { formatReportMonth } from "../reportFormat";

/** One line, e.g. "Feb 2026 · 41.7% compliance". */
export function formatComplianceTooltip(point: KpiComplianceValue): string {
  const month = formatReportMonth(point.month);
  if (point.weightedPct === null) return `${month} · no KPI reported`;
  return `${month} · ${point.weightedPct}% compliance`;
}

/** The chart's `aria-label`: the window, the range average, how much of the
 *  window is unreported, and a pointer to the tables that carry the per-month
 *  figures. The tables are the accessible path to exact values, so the label
 *  deliberately does not read out twelve months of numbers. */
export function describeComplianceChart(
  series: KpiComplianceValue[],
  rangeWeightedPct: number | null,
): string {
  const first = formatReportMonth(series[0].month);
  const last = formatReportMonth(series[series.length - 1].month);
  const unreported = series.filter(
    (point) => point.weightedPct === null,
  ).length;

  const average =
    rangeWeightedPct === null
      ? "no month in the range has a reported result"
      : `averaging ${rangeWeightedPct}% across the range`;

  const gap =
    unreported === 0 || rangeWeightedPct === null
      ? ""
      : `${unreported} month${unreported === 1 ? " has" : "s have"} no reported result. `;

  return (
    `Monthly KPI compliance, ${first} to ${last}, ${average}. ` +
    gap +
    `Exact monthly figures follow in the heatmap and detail tables.`
  );
}
